/**
 * src/data/productCatalog.js
 *
 * Single source of truth for every Bow Tie Kreative offering shown on the site.
 *
 * Consumers:
 *  - <MegaMenu />   — renders only items where `inMegaMenu !== false`, grouped
 *                     by `megaMenuGroup`.
 *  - <Footer />     — renders only items where `inFooter` is true (currently
 *                     curated manually because we only show ~10 in the
 *                     Products column).
 *  - <MarketplacePage /> — renders every item where `inMarketplace !== false`,
 *                          filtered by `category`.
 *
 * Adding a new product:
 *   1. Append one object to PRODUCTS below.
 *   2. Decide which views it should appear in via the flags.
 *   3. That's it — all three surfaces pick it up automatically.
 *
 * Schema:
 *   id              kebab-case unique key (used in data-testid and React key)
 *   to              react-router path
 *   title           short display name
 *   sub             one-line description (≤ 90 chars)
 *   img             public-relative image path (will be prefixed with PUBLIC_URL)
 *   category        marketplace filter id: service | workshop | course | book |
 *                   membership | booking | free
 *   price           informational badge (e.g. "$10,000", "Monthly", "FREE")
 *   badge           visual flag: "NEW" | "FREE" | "MEMBERS" | "AFFILIATE" | null
 *   megaMenuGroup   one of: "Done-For-You" | "Workshops & Courses" |
 *                   "Books & Briefs" | "Talk to Ryan"
 *   megaMenuTag     short tagline shown in MegaMenu cards (≤ 40 chars)
 *   inMegaMenu      default true. Set false to hide from MegaMenu.
 *   inFooter        default false. Set true to surface in Footer products list.
 *   inMarketplace   default true. Set false to hide from /marketplace.
 */

const PUB = process.env.PUBLIC_URL || "";

/* eslint-disable max-lines */
export const PRODUCTS = [

  // ------------------------------------------------------------------------
  // Done-For-You Services
  // ------------------------------------------------------------------------
  {
    id: "websites",
    to: "/",
    title: "Bow Tie Websites",
    sub: "Lite → Classic Knot → Windsor Growth → Black Label Studio",
    img: `${PUB}/menu/websites-hero.jpg`,
    category: "service",
    price: "From $97/mo",
    megaMenuGroup: "Done-For-You",
    megaMenuTag: "Bow Tie Lite → Black Label Studio",
    // Bow Tie Websites is on the home page, no separate footer entry needed.
    inFooter: false,
  },
  {
    id: "funnels",
    to: "/funnels",
    title: "Sales Funnels — Concierge",
    sub: "Start, Plus, Pro, Elite — funnels managed end-to-end",
    img: `${PUB}/menu/funnels-hero.jpg`,
    category: "service",
    price: "From $1,997/mo",
    megaMenuGroup: "Done-For-You",
    megaMenuTag: "Concierge Start → Elite",
    inFooter: true,
    footerLabel: "Sales Funnels",
  },
  {
    id: "website-revenue-regeneration",
    to: "/website-revenue-regeneration",
    title: "90-Day Implementation",
    sub: "Website Revenue Regeneration — done with you, in 90 days",
    img: `${PUB}/website-revenue-regeneration/hero-box.png`,
    category: "service",
    price: "Custom",
    megaMenuGroup: "Done-For-You",
    megaMenuTag: "Website Revenue Regeneration",
    inFooter: true,
  },
  {
    id: "unlimited-design-partner",
    to: "/unlimited-design-partner",
    title: "Unlimited Design Partner",
    sub: "Your on-demand design partner — unlimited requests, one active at a time",
    img: `${PUB}/unlimited-design-partner/hero-box.png`,
    category: "service",
    price: "Monthly",
    megaMenuGroup: "Done-For-You",
    megaMenuTag: "Your on-demand design partner",
    inFooter: true,
    footerLabel: "Unlimited Design",
  },
  {
    id: "complete-brand-identity-system",
    to: "/complete-brand-identity-system",
    title: "Complete Brand Identity System",
    sub: "Foundation to launch — full done-for-you brand build",
    img: `${PUB}/complete-brand-identity-system/hero-box.png`,
    category: "service",
    price: "$10,000",
    badge: "NEW",
    megaMenuGroup: "Done-For-You",
    megaMenuTag: "Foundation to launch · $10K",
    inFooter: true,
  },
  {
    id: "growth-points",
    to: "/growth-points",
    title: "GrowthPoints",
    sub: "Point-based marketing system — flex points across any channel",
    img: `${PUB}/growth-points/hero.png`,
    category: "service",
    price: "From $497/mo",
    badge: "NEW",
    megaMenuGroup: "Done-For-You",
    megaMenuTag: "Point-based marketing system",
    inFooter: true,
  },

  // ------------------------------------------------------------------------
  // Workshops & Courses
  // ------------------------------------------------------------------------
  {
    id: "workshop",
    to: "/workshop",
    title: "3-Day Website Workshop",
    sub: "Build, launch, and walk away with a live website in three days",
    img: `${PUB}/workshop/hero-box.png`,
    category: "workshop",
    price: "$4,997",
    megaMenuGroup: "Workshops & Courses",
    megaMenuTag: "Build your website in 3 days",
    inFooter: true,
  },
  {
    id: "brand-transformation-workshop",
    to: "/brand-transformation-workshop",
    title: "Brand Transformation Workshop",
    sub: "6-session strategic intensive — strategy, positioning, messaging",
    img: `${PUB}/brand-transformation-workshop/hero-box.png`,
    category: "workshop",
    price: "$9,997",
    megaMenuGroup: "Workshops & Courses",
    megaMenuTag: "6-session strategic intensive",
    inFooter: true,
  },
  {
    id: "social-media-virality",
    to: "/social-media-virality",
    title: "Social Media Virality",
    sub: "Course on engineering virality across platforms (with book bundle)",
    img: `${PUB}/social-media/hero-box.jpg`,
    category: "course",
    price: "$47",
    megaMenuGroup: "Workshops & Courses",
    megaMenuTag: "Course + book bundle",
    inFooter: true,
  },
  {
    id: "seo-course-2026",
    to: "/seo-course",
    title: "Master SEO in 2026 — The Bow Tie Method",
    sub: "Premium 12-module course + manual · GEO, AI Overviews, schema, certificate",
    img: "/seo-course/hero-box.png",
    category: "course",
    price: "$197",
    badge: "NEW",
    megaMenuGroup: "Workshops & Courses",
    megaMenuTag: "Premium SEO Course + Manual",
    inFooter: true,
  },
  {
    id: "social-media-uncensored",
    to: "/social-media-uncensored",
    title: "Social Media Uncensored",
    sub: "No-fluff course on platform mechanics and tactics — by Ryan Perez",
    img: `${PUB}/social-media-uncensored/hero-book.jpg`,
    category: "course",
    price: "$9.99",
    megaMenuGroup: "Books & Briefs",
    megaMenuTag: "$9.99 ebook by Ryan Perez",
    inFooter: true,
  },

  // ------------------------------------------------------------------------
  // One-shot sprints
  // ------------------------------------------------------------------------
  {
    id: "brand-naming-sprint",
    to: "/brand-naming-sprint",
    title: "The Brand Naming Sprint",
    sub: "15 brand-name candidates · 24-hour delivery · domain + linguistic + trademark notes",
    img: `${PUB}/products/brand-naming-sprint/hero-box.jpg`,
    category: "service",
    price: "$97",
    badge: "NEW",
    megaMenuGroup: "Done-For-You",
    megaMenuTag: "15 names · 24 hours · $97",
    inFooter: true,
    footerLabel: "Brand Naming Sprint",
  },

  // ------------------------------------------------------------------------
  // Books & Briefs
  // ------------------------------------------------------------------------
  {
    id: "tie-up-loose-ends",
    to: "/tie-up-loose-ends",
    title: "Tie Up Loose Ends",
    sub: "Free strategic guide for tightening website + marketing decisions",
    img: `${PUB}/tie-up-loose-ends/hero.png`,
    category: "free",
    price: "FREE",
    badge: "FREE",
    megaMenuGroup: "Books & Briefs",
    megaMenuTag: "Free strategic website guide",
    inFooter: true,
  },
  {
    id: "brand-identity-ebook",
    to: "/brand-identity-ebook",
    title: "Brand Identity Ebook",
    sub: "Strategic framework for building brands that resonate and endure",
    img: `${PUB}/brand-identity-ebook/hero-book.jpg`,
    category: "book",
    price: "$19",
    megaMenuGroup: "Books & Briefs",
    megaMenuTag: "Build a brand that converts",
    inFooter: true,
  },
  {
    id: "creative-brief",
    to: "/creative-brief",
    title: "Creative Brief",
    sub: "Guided 10-type project brief → strategy + concept delivery",
    img: `${PUB}/creative-brief/hero/YBjW8lBi.png`,
    category: "book",
    price: "$197",
    megaMenuGroup: "Books & Briefs",
    megaMenuTag: "Guided 10-type project brief",
    inFooter: true,
  },

  // ------------------------------------------------------------------------
  // Membership
  // ------------------------------------------------------------------------
  {
    id: "vault",
    to: "/vault",
    title: "The Vault",
    sub: "Lifetime resource library + private community access",
    img: `${PUB}/vault/hero-box.jpg`,
    category: "membership",
    price: "$497 lifetime",
    // Vault is shown as a category-card on /marketplace and as the "Vault Login"
    // entry below in MegaMenu — so hide this entry from the mega menu to avoid
    // duplication.
    inMegaMenu: false,
    inFooter: true,
  },

  // ------------------------------------------------------------------------
  // Talk to Ryan / Bookings
  // ------------------------------------------------------------------------
  {
    id: "free-consultation",
    to: "/book-a-call/free-consultation",
    title: "Free Consultation",
    sub: "15-minute discovery call with Ryan — map your next step",
    img: `${PUB}/book-a-call/hero-boxes.jpg`,
    category: "booking",
    price: "FREE",
    badge: "FREE",
    // Free-consultation rolls up under the broader "Book a Call" MegaMenu entry.
    inMegaMenu: false,
  },
  {
    id: "strategy-call",
    to: "/book-a-call/strategy-call",
    title: "60-Minute Strategy Call",
    sub: "Focused strategy session — live diagnosis + action plan",
    img: `${PUB}/book-a-call/hero-boxes.jpg`,
    category: "booking",
    price: "$497",
    inMegaMenu: false,
  },
  {
    id: "marketing-audit",
    to: "/book-a-call/marketing-audit",
    title: "Marketing Audit",
    sub: "Site, offer & funnel review — prioritized recommendations",
    img: `${PUB}/book-a-call/hero-boxes.jpg`,
    category: "booking",
    price: "$997",
    inMegaMenu: false,
  },
  {
    id: "book-a-call",
    to: "/book-a-call",
    title: "Book a Call",
    sub: "Audits + strategy sessions with Ryan",
    img: `${PUB}/book-a-call/hero-boxes.jpg`,
    category: "booking",
    megaMenuGroup: "Talk to Ryan",
    megaMenuTag: "Audits + strategy sessions",
    // This is a router/hub page — the marketplace already lists each booking
    // type separately, so hide the hub from /marketplace.
    inMarketplace: false,
    inFooter: true,
  },
  {
    id: "the-hustle-zone",
    to: "/the-hustle-zone",
    title: "The Hustle Zone Podcast",
    sub: "Apply to be a guest — share your story with our audience",
    img: `${PUB}/the-hustle-zone/lineup.jpg`,
    category: "booking",
    price: "Apply",
    megaMenuGroup: "Talk to Ryan",
    megaMenuTag: "Apply to be a guest",
    inFooter: true,
  },
  {
    id: "gold-tie-program",
    to: "/gold-tie-program",
    title: "The Gold Tie Program",
    sub: "Affiliate program — earn 15% on referred services",
    img: `${PUB}/gold-tie-program/hero.png`,
    category: "booking",
    price: "Affiliate",
    badge: "NEW",
    megaMenuGroup: "Talk to Ryan",
    megaMenuTag: "Earn 15% — affiliate program",
    inFooter: true,
    footerBadge: "AFFILIATE",
  },
  {
    id: "vault-login",
    to: "/vault/login",
    title: "Vault Login",
    sub: "Members — open the community",
    img: `${PUB}/vault/hero-box.jpg`,
    category: "membership",
    badge: "MEMBERS",
    megaMenuGroup: "Talk to Ryan",
    megaMenuTag: "Members — open the community",
    inMarketplace: false,
  },
];

/**
 * Group MegaMenu items by `megaMenuGroup` preserving original ordering.
 * Returns: [{ label, items: [...] }, ...]
 */
export const MEGA_MENU_GROUPS = (() => {
  const groupOrder = ["Done-For-You", "Workshops & Courses", "Books & Briefs", "Talk to Ryan"];
  const groups = Object.fromEntries(groupOrder.map((g) => [g, []]));
  for (const p of PRODUCTS) {
    if (p.inMegaMenu === false || !p.megaMenuGroup) continue;
    if (!groups[p.megaMenuGroup]) continue;
    groups[p.megaMenuGroup].push({
      to: p.to,
      label: p.title === "Complete Brand Identity System" ? "Brand Identity System" : p.title,
      tag: p.megaMenuTag || p.sub,
      img: p.img,
      badge: p.badge,
    });
  }
  // Append two virtual groups: Digital Stem Cell™ products (sales pages)
  // and Utilities (actual interactive tools for unlocked users).
  const arr = groupOrder.map((label) => ({ label, items: groups[label] }));
  arr.push({
    label: "Digital Stem Cell™",
    items: [
      { to: "/seo-report",           label: "Free SEO Report",         tag: "Instant 12-category website scan",       img: `${PUB}/dsc-images/heroes/seo-report.png`,         badge: "FREE" },
      { to: "/marketing-audit-free", label: "Free Marketing Audit",    tag: "24-question marketing scorecard",         img: `${PUB}/dsc-images/heroes/marketing-audit-free.png`, badge: "FREE" },
      { to: "/possibility-lattice",  label: "Strategy Engine",         tag: "216 strategic alternatives per problem", img: `${PUB}/dsc-images/heroes/possibility-lattice.png`, badge: "BETA" },
      { to: "/prediction-engine",    label: "Prediction Engine",       tag: "216 forecasted outcomes per scenario",   img: `${PUB}/dsc-images/heroes/prediction-engine.png`,  badge: "NEW"  },
    ],
  });
  arr.push({
    label: "Utilities & Help",
    items: [
      { to: "/utilities/seo-report",        label: "SEO Scanner",        tag: "Run a website scan",         img: `${PUB}/dsc-images/heroes/seo-report.png`,         badge: "TOOL" },
      { to: "/utilities/marketing-audit",   label: "Marketing Audit",    tag: "Take the 24-question audit", img: `${PUB}/dsc-images/heroes/marketing-audit-free.png`, badge: "TOOL" },
      { to: "/utilities/strategy-engine",   label: "Strategy Engine",    tag: "Generate the lattice",       img: `${PUB}/dsc-images/heroes/possibility-lattice.png`, badge: "TOOL" },
      { to: "/utilities/prediction-engine", label: "Prediction Engine",  tag: "Forecast outcomes",          img: `${PUB}/dsc-images/heroes/prediction-engine.png`,  badge: "TOOL" },
      { to: "/docs",                        label: "Documentation",      tag: "Help center · how to use every product", img: `${PUB}/seo-course/hero-box.png`,        badge: "HELP" },
    ],
  });
  return arr;
})();

/** Products eligible for the Marketplace grid. */
export const MARKETPLACE_PRODUCTS = PRODUCTS.filter((p) => p.inMarketplace !== false);

/** Products curated for the Footer "Products" column. Each entry includes a
 *  consistent `testid` derived from the product id, plus an optional
 *  `footerLabel` override and `footerBadge` (defaults to product.badge).
 */
export const FOOTER_PRODUCTS = PRODUCTS
  .filter((p) => p.inFooter)
  .map((p) => ({
    to: p.to,
    label: p.footerLabel || p.title,
    testid: `footer-product-${p.id}`,
    badge: p.footerBadge || p.badge,
  }));

/** Static-rendered virtual entries appended to the footer list (not real
 *  products — e.g. the /marketplace meta page).
 */
export const FOOTER_EXTRAS = [
  { to: "/marketplace", label: "Marketplace", testid: "footer-product-marketplace", badge: "NEW" },
  { to: "/possibility-lattice", label: "Digital Stem Cell Strategy Engine", testid: "footer-product-possibility-lattice", badge: "BETA" },
  { to: "/prediction-engine",   label: "Digital Stem Cell Prediction Engine", testid: "footer-product-prediction-engine", badge: "NEW" },
  { to: "/seo-report",          label: "Free SEO Report",                     testid: "footer-product-seo-report", badge: "FREE" },
  { to: "/marketing-audit-free",label: "Free Marketing Audit",                testid: "footer-product-marketing-audit-free", badge: "FREE" },
];
